import React from 'react';
import { createRoot } from 'react-dom/client';
import { Spin } from 'antd';

import NextApp from './NextApp';
import registerServiceWorker from './registerServiceWorker';
import FaviconSpinner from './components/common/FaviconSpinner';


// Import Tailwind CSS
// import './tailwind.css'
import './tailwind.output.css';
import './styles/responsive-global.css';
import Providers from './Providers/Providers';



Spin.setDefaultIndicator(<FaviconSpinner size={38} style={{ padding: 0 }} />);

// Helper function to detect chunk loading errors
const isChunkError = (error, filename) => {
  if (!error) return false;

  const errorMessage = error.message || '';
  const errorName = error.name || '';
  const isChunkFile = filename?.includes('.chunk.js') || filename?.includes('chunk.js');
  const isUnexpectedToken = errorMessage.includes('Unexpected token') && isChunkFile;
  const isChunkLoadError =
    errorMessage.includes('Loading chunk') ||
    errorMessage.includes('ChunkLoadError') ||
    errorName === 'ChunkLoadError';

  return (
    isChunkLoadError || isUnexpectedToken || (isChunkFile && errorMessage.includes('SyntaxError'))
  );
};

// Global error handler for chunk loading errors
window.addEventListener(
  'error',
  (event) => {
    const error = event.error || event;
    const filename = event.filename || event.target?.src || '';

    if (isChunkError(error, filename)) {
      console.error('[Global ChunkLoadError]', {
        error,
        filename,
        message: error.message,
        stack: error.stack,
      });

      // Prevent default error handling
      event.preventDefault();
      event.stopPropagation();

      // Clear cache and reload
      if ('caches' in window) {
        caches.keys().then((names) => {
          names.forEach((name) => caches.delete(name));
        });
      }

      // Show user-friendly message and reload
      setTimeout(() => {
        const shouldReload = window.confirm(
          'พบปัญหาในการโหลดไฟล์ของแอปพลิเคชัน กรุณารีเฟรชหน้าเว็บเพื่อโหลดเวอร์ชันล่าสุด\n\n' +
          'Application loading error. Please refresh the page to load the latest version.'
        );
        if (shouldReload) {
          // Force hard reload to clear cache
          window.location.href = window.location.href.split('#')[0] + '?v=' + Date.now();
        }
      }, 100);

      return false;
    }
  },
  true
); // Use capture phase to catch errors early

// Handle unhandled promise rejections (for async chunk loading)
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason;

  if (isChunkError(error)) {
    console.error('[Unhandled ChunkLoadError]', error);
    event.preventDefault();

    // Clear cache
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }

    setTimeout(() => {
      const shouldReload = window.confirm(
        'พบปัญหาในการโหลดไฟล์ของแอปพลิเคชัน กรุณารีเฟรชหน้าเว็บเพื่อโหลดเวอร์ชันล่าสุด\n\n' +
        'Application loading error. Please refresh the page to load the latest version.'
      );
      if (shouldReload) {
        window.location.href = window.location.href.split('#')[0] + '?v=' + Date.now();
      }
    }, 100);
  }
});

// Intercept fetch requests for chunk files and manifest to detect HTML responses
const originalFetch = window.fetch;
window.fetch = function (...args) {
  const url = args[0];
  const urlString = typeof url === 'string' ? url : url?.url || '';
  const isChunkFile = urlString.includes('.chunk.js') || urlString.includes('chunk.js');
  const isManifest = urlString.includes('manifest.json');

  if (isChunkFile || isManifest) {
    return originalFetch
      .apply(this, args)
      .then((response) => {
        // Check if response is HTML instead of expected content type
        const contentType = response.headers.get('content-type') || '';
        const isHTML = contentType.includes('text/html');
        const isJSON = contentType.includes('application/json');

        // For chunk files, we expect JavaScript
        if (isChunkFile && (isHTML || (isJSON && !urlString.includes('.json')))) {
          return response.text().then((text) => {
            const textTrimmed = text.trim();
            if (
              textTrimmed.startsWith('<!DOCTYPE') ||
              textTrimmed.startsWith('<html') ||
              (textTrimmed.startsWith('{') && isHTML)
            ) {
              console.error(
                '[ChunkLoadError] Received HTML/JSON instead of JavaScript:',
                urlString
              );
              throw new Error(`ChunkLoadError: Failed to load chunk file ${urlString}`);
            }
            return new Response(text, response);
          });
        }

        // For manifest, we expect JSON
        if (isManifest && isHTML) {
          return response.text().then((text) => {
            const textTrimmed = text.trim();
            if (textTrimmed.startsWith('<!DOCTYPE') || textTrimmed.startsWith('<html')) {
              console.error('[ManifestError] Received HTML instead of JSON:', urlString);
              // Don't throw for manifest errors, just log
              console.warn('[ManifestError] Manifest file not found, continuing without it');
              return new Response('{}', {
                status: 200,
                headers: { 'Content-Type': 'application/json' },
              });
            }
            return new Response(text, response);
          });
        }

        return response;
      })
      .catch((error) => {
        if (
          isChunkFile &&
          (error.message?.includes('ChunkLoadError') ||
            error.message?.includes('Failed to load chunk'))
        ) {
          console.error('[ChunkLoadError] Fetch failed:', urlString, error);
          // Clear cache and reload
          if ('caches' in window) {
            caches.keys().then((names) => {
              names.forEach((name) => caches.delete(name));
            });
          }
          setTimeout(() => {
            window.location.href = window.location.href.split('#')[0] + '?v=' + Date.now();
          }, 100);
        } else if (isManifest) {
          // For manifest errors, just log and continue
          console.warn('[ManifestError] Failed to load manifest:', urlString, error);
          return new Response('{}', {
            status: 200,
            headers: { 'Content-Type': 'application/json' },
          });
        }
        throw error;
      });
  }

  return originalFetch.apply(this, args);
};

// Get root element
const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element not found');
}

// Create root
const root = createRoot(container);

// Render function
const render = (Component) => {
  root.render(
    <Component />
  );
};

// Do this once
//****Uncomment inside when production
registerServiceWorker();

// Render once
render(NextApp);

// Webpack Hot Module Replacement API
if (module.hot) {
  module.hot.accept('./NextApp', () => {
    render(NextApp);
  });
}
